import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@project/ui";
import { Button, Icon } from "@project/ui";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { localeToIntlTag } from "@project/i18n";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import {
  fetchDashboardAnalytics,
  homeExpenseQueryKeys,
} from "@apps/dashboard/lib/home-expense-api";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

const ANALYTICS_OPTS = { months: 12, minMonthsForInsight: 6 } as const;

export default function HouseAnalyticsPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  useSyncHouseFromParams(houseId);

  const analyticsQuery = useQuery({
    queryKey:
      houseId != null
        ? homeExpenseQueryKeys.dashboardAnalytics(locale, houseId, ANALYTICS_OPTS)
        : ["dashboard-analytics", "noop"],
    queryFn: () => {
      if (houseId == null) throw new Error("missing houseId");
      return fetchDashboardAnalytics(locale, houseId, ANALYTICS_OPTS);
    },
    enabled: houseId != null,
  });

  const data = analyticsQuery.data;

  const chartRows = useMemo(() => {
    if (!data) return [];
    return data.months.map((m) => ({
      ...m,
      monthShort: m.yearMonth.slice(5),
      avgLine: data.avgSpentMinor,
    }));
  }, [data]);

  const overspendLeader = useMemo(() => {
    if (!data?.categoryInsights.length) return null;
    return [...data.categoryInsights].sort(
      (a, b) => b.totalOverspendMinor - a.totalOverspendMinor,
    )[0];
  }, [data]);

  const reviewList = useMemo(
    () => data?.categoryInsights.filter((c) => c.suggestReview) ?? [],
    [data],
  );
  const reduceList = useMemo(
    () => data?.categoryInsights.filter((c) => c.suggestReduceBudget) ?? [],
    [data],
  );

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <p className="text-on-surface-variant">{t("dashboardUi.analytics.missingHouse")}</p>
      </div>
    );
  }

  if (analyticsQuery.isLoading) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <LoadingState />
      </div>
    );
  }

  if (analyticsQuery.isError || !data) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState onRetry={() => void analyticsQuery.refetch()} />
      </div>
    );
  }

  const currency = data.currency;
  const fmt = (minor: number) => formatMinorAsCurrency(minor, currency, locale);
  const hasAnySpend = data.months.some((m) => m.spentMinor > 0);

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button variant="ghost" className="-ml-2 mb-2 h-auto px-2 py-1" asChild>
            <Link
              to={dashboardPaths.houseBudget(houseId)}
              className="inline-flex items-center gap-2 font-label text-label-md text-on-surface-variant"
            >
              <Icon name="ArrowLeft" className="size-4" strokeWidth={2} aria-hidden />
              {t("dashboardUi.analytics.backToBudget")}
            </Link>
          </Button>
          <h1 className="font-headline text-headline-sm font-bold text-primary">
            {t("dashboardUi.pages.houseAnalytics")}
          </h1>
          <p className="mt-2 max-w-2xl font-body text-body-md text-on-surface-variant">
            {t("dashboardUi.analytics.subtitle", { count: data.windowMonths })}
          </p>
        </div>
      </div>

      {!hasAnySpend ? (
        <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-low p-10 text-center">
          <Icon
            name="LineChart"
            className="mx-auto mb-4 size-12 text-on-surface-variant/40"
            strokeWidth={1.25}
            aria-hidden
          />
          <p className="font-body text-body-md text-on-surface-variant">
            {t("dashboardUi.analytics.empty")}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-10 rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-4 shadow-sm sm:p-6">
            <h2 className="mb-4 font-headline text-lg font-bold text-primary">
              {t("dashboardUi.analytics.chartTitle")}
            </h2>
            <div className="h-[320px] w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-outline-variant/20" />
                  <XAxis dataKey="monthShort" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) =>
                      new Intl.NumberFormat(localeToIntlTag(locale), {
                        notation: "compact",
                        maximumFractionDigits: 1,
                      }).format(Number(v) / 100)
                    }
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      const raw = Array.isArray(value) ? value[0] : value;
                      const n = typeof raw === "number" ? raw : Number(raw);
                      const key = String(name);
                      const label =
                        key === "spentMinor"
                          ? t("dashboardUi.analytics.chartPaid")
                          : key === "plannedMinor"
                            ? t("dashboardUi.analytics.chartPlanned")
                            : t("dashboardUi.analytics.chartAvgLine");
                      return [fmt(Number.isFinite(n) ? n : 0), label];
                    }}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.yearMonth != null
                        ? String(payload[0].payload.yearMonth)
                        : ""
                    }
                  />
                  <Legend
                    formatter={(value) =>
                      value === "spentMinor"
                        ? t("dashboardUi.analytics.chartPaid")
                        : value === "plannedMinor"
                          ? t("dashboardUi.analytics.chartPlanned")
                          : t("dashboardUi.analytics.chartAvgLine")
                    }
                  />
                  <Bar
                    dataKey="spentMinor"
                    name="spentMinor"
                    fill="#00236f"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="plannedMinor"
                    name="plannedMinor"
                    fill="#006c49"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="avgLine"
                    name="avgLine"
                    stroke="#c2410c"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mb-10 grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-6">
              <h2 className="font-headline text-sm font-bold uppercase tracking-widest text-on-surface-variant">
                {t("dashboardUi.analytics.peakMonthTitle")}
              </h2>
              <p className="mt-3 font-headline text-2xl font-bold text-primary">
                {data.peakSpendMonth != null
                  ? t("dashboardUi.analytics.peakMonthBody", {
                      month: data.peakSpendMonth,
                      amount: fmt(
                        data.months.find((m) => m.yearMonth === data.peakSpendMonth)?.spentMinor ??
                          0,
                      ),
                    })
                  : t("dashboardUi.analytics.peakMonthNone")}
              </p>
            </div>
            <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-6">
              <h2 className="font-headline text-sm font-bold uppercase tracking-widest text-on-surface-variant">
                {t("dashboardUi.analytics.avgSpendTitle")}
              </h2>
              <p className="mt-3 font-headline text-2xl font-bold text-primary">
                {fmt(data.avgSpentMinor)}
              </p>
              <p className="mt-2 font-body text-body-sm text-on-surface-variant">
                {t("dashboardUi.analytics.avgSpendHint")}
              </p>
            </div>
          </div>

          <div className="mb-10 rounded-2xl border border-outline-variant/15 bg-surface-container-low p-6">
            <h2 className="mb-4 font-headline text-lg font-bold text-primary">
              {t("dashboardUi.analytics.topCategoriesTitle", { count: data.windowMonths })}
            </h2>
            <ul className="divide-y divide-outline-variant/10">
              {data.topSpendingCategories.map((c) => (
                <li
                  key={c.categoryId}
                  className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <span className="font-body text-body-md text-on-surface">{c.categoryName}</span>
                  <span className="shrink-0 font-headline font-semibold text-primary">
                    {fmt(c.totalSpentMinor)}
                  </span>
                </li>
              ))}
            </ul>
            {data.otherSpendingMinor != null && data.otherSpendingMinor > 0 ? (
              <p className="mt-4 font-body text-body-sm text-on-surface-variant">
                {t("dashboardUi.analytics.otherCategories", {
                  amount: fmt(data.otherSpendingMinor),
                })}
              </p>
            ) : null}
          </div>

          {overspendLeader != null && overspendLeader.totalOverspendMinor > 0 ? (
            <div className="mb-10 rounded-2xl border border-secondary/20 bg-secondary-container/15 p-6">
              <h2 className="font-headline text-lg font-bold text-on-secondary-container">
                {t("dashboardUi.analytics.overspendLeaderTitle", { count: data.windowMonths })}
              </h2>
              <p className="mt-2 font-body text-body-md text-on-secondary-container/90">
                {t("dashboardUi.analytics.overspendLeaderBody", {
                  name: overspendLeader.categoryName,
                  amount: fmt(overspendLeader.totalOverspendMinor),
                  count: data.windowMonths,
                })}
              </p>
            </div>
          ) : null}

          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-6">
              <h2 className="mb-2 font-headline text-lg font-bold text-primary">
                {t("dashboardUi.analytics.reviewTitle")}
              </h2>
              <p className="mb-4 font-body text-body-sm text-on-surface-variant">
                {t("dashboardUi.analytics.reviewHint")}
              </p>
              {reviewList.length === 0 ? (
                <p className="font-body text-body-md text-on-surface-variant">
                  {t("dashboardUi.analytics.reviewEmpty")}
                </p>
              ) : (
                <ul className="list-inside list-disc space-y-2 font-body text-body-md">
                  {reviewList.map((c) => (
                    <li key={c.categoryId}>{c.categoryName}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-6">
              <h2 className="mb-2 font-headline text-lg font-bold text-primary">
                {t("dashboardUi.analytics.reduceTitle")}
              </h2>
              <p className="mb-4 font-body text-body-sm text-on-surface-variant">
                {t("dashboardUi.analytics.reduceHint")}
              </p>
              {reduceList.length === 0 ? (
                <p className="font-body text-body-md text-on-surface-variant">
                  {t("dashboardUi.analytics.reduceEmpty")}
                </p>
              ) : (
                <ul className="list-inside list-disc space-y-2 font-body text-body-md">
                  {reduceList.map((c) => (
                    <li key={c.categoryId}>{c.categoryName}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
